import { Injectable } from '@angular/core';
import { HttpParams } from '@angular/common/http';
import { BehaviorSubject, Subject } from 'rxjs';
import { catchError, filter, map, repeat, take } from 'rxjs/operators';
import { saveAs } from 'file-saver';
import * as Excel from 'exceljs';

import { TranslateService } from './../api/translate.service';
import { ApiService } from './../api.service';
import { HelperService } from '../utils/helper.service';
import { ErrorHandlerService } from './error-handler.service';
import uuid from '../utils/uuid';
import { TransactionStatusFilters } from '../models/transactions-enum';
import { ReportingFilterObjInit } from '../interfaces/ag-grid-interfaces';
import { DataObject } from '../interfaces/data-objects';
import { DisplayFormat } from '../utils/date-formatting';

@Injectable({
  providedIn: 'root'
})
export class ReportingService {
  refreshGridSubject = new Subject();
  tableViewCheckSubject = new Subject();

  private gridSpinner = new BehaviorSubject(true);
  gridSpinner$ = this.gridSpinner.asObservable();

  private reportTitleSubject: Subject<string> = new Subject();
  reportTitleSubject$ = this.reportTitleSubject.asObservable();
  private currentReportDescription: string = '';

  dateFormat: DisplayFormat = 'yyyy-mm-dd'; // ISO Format as default for all countries, can be updated based on user locale if needed

  constructor(
    private api: ApiService,
    private translateService: TranslateService,
    private helperService: HelperService,
    private errorHandler: ErrorHandlerService) {
  }

  setgridSpinner(bool: boolean): void {
    this.gridSpinner.next(bool);
  }

  setReportTitleSubject(title: string): void {
    this.reportTitleSubject.next(title);
  }

  setReportDescription(description: string): void {
    this.currentReportDescription = description;
  }

  getReportDescription(): string {
    return this.currentReportDescription;
  }

  exportAsExcelFile(json: any[], headers: any[], view: any,
    filterObject: { startDate: any; endDate: any }, status: any, searchParam: any): void {
    const workbook: Excel.Workbook = new Excel.Workbook();
    const sheet = workbook.addWorksheet(this.translateService.getTranslation('FINISHLINE_REPORTING'),
      { properties: { tabColor: { argb: 'FFCCFFCC' } } });

    const criteriaSheet = workbook.addWorksheet(this.translateService.getTranslation('FILTER_CRITERIA'),
      { properties: { tabColor: { argb: 'FFC0000' } } });

    sheet.columns = headers;
    sheet.addRows(json);
    sheet.getRow(1).font = { name: 'Calibri', family: 4, size: 12, underline: 'double', bold: true };
    sheet.getRow(1).alignment = { vertical: 'middle', horizontal: 'center' };
    sheet.getRow(1).fill = {
      type: 'pattern',
      pattern: 'darkTrellis',
      fgColor: { argb: 'FFCCFFCC' },
    };
    switch (status) {
      case 'landing':
        // Format columns specific to landing page data
        sheet.getColumn('cbre_fee').numFmt = '#,##0.00';
        break;
      case 'wip':
      case 'submitted':
          sheet.getColumn('size').numFmt = '#,##0.00';
          sheet.getColumn('rooms').numFmt = '#,##0.00';
          sheet.getColumn('units').numFmt = '#,##0.00';
          sheet.getColumn('area').numFmt = '#,##0.00';
          sheet.getColumn('cbre_fee').numFmt = '#,##0.00';
          sheet.getColumn('invoice_value_total').numFmt = '#,##0.00';
          sheet.getColumn('invoice_value_split').numFmt = '#,##0.00';
          sheet.getColumn('flat_amount').numFmt = '#,##0.00';
          sheet.getColumn('cbre_revenue').numFmt = '#,##0.00';
          sheet.getColumn('actual_fee').numFmt = '#,##0.00';
        break;
      case 'closed':
        sheet.getColumn('size').numFmt = '#,##0.00';
        sheet.getColumn('rooms').numFmt = '#,##0.00';
        sheet.getColumn('units').numFmt = '#,##0.00';
        sheet.getColumn('area').numFmt = '#,##0.00';
        sheet.getColumn('cbre_fee_total').numFmt = '#,##0.00';
        sheet.getColumn('invoice_value_total').numFmt = '#,##0.00';
        sheet.getColumn('invoice_value_split').numFmt = '#,##0.00';
        sheet.getColumn('cbre_fee_fumo_balance').numFmt = '#,##0.00';
        sheet.getColumn('cbre_revenue_total').numFmt = '#,##0.00';
        sheet.getColumn('cbre_revenue_fumo_balance').numFmt = '#,##0.00';
        if (view !== 'small') {
          sheet.getColumn('balance').numFmt = '#,##0.00';
        }
        break;
    }

    /** Apply Date Format */
    sheet.columns.forEach((column: any) => {
      if (`${column.header}`.toLowerCase().includes('date')) {
        column.numFmt = this.dateFormat;

        // Will attempt to convert date strings to Date objects for proper Excel formatting, if conversion fails it will keep the original value
        column?.values.forEach((value: any, index: number) => {
          if (index === 1) return; // Skip header row
          if (value && typeof value === 'string') {
            const dateValue = new Date(value);
            if (Number.isNaN(dateValue.getTime())) {
              sheet.getCell(`${column.letter}${index}`).value = value; // Keep original value if it's not a valid date
            } else {
              sheet.getCell(`${column.letter}${index}`).value = dateValue;
            }
          }
        });
      }
    });

    criteriaSheet.columns = this.getCriteriaColumnHeaders();
    criteriaSheet.getRow(2).values = [this.translateService.getTranslation('START_DATE'), filterObject.startDate];
    criteriaSheet.getRow(3).values = [this.translateService.getTranslation('END_DATE'), filterObject.endDate];
    criteriaSheet.getRow(4).values = [this.translateService.getTranslation('TABLE_VIEW'), view];
    criteriaSheet.getRow(5).values = [this.translateService.getTranslation('STATUS'), status];
    if (searchParam) {
      criteriaSheet.getRow(6).values = [this.translateService.getTranslation('SEARCH_KEYWORD'), searchParam];
    }
    criteriaSheet.getRow(1).font = { name: 'Calibri', family: 4, size: 12, underline: 'double', bold: true };

    workbook.xlsx.writeBuffer().then((data: any) => {
      const blob = new Blob([data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      saveAs(blob, this.translateService.getTranslation('REPORTS_XLSX'));
    });
  }

  getCriteriaColumnHeaders(): any[] {
    return ['KEY', 'VALUE'].map(headerKey => {
      return {
        header: `${this.translateService.getTranslation(headerKey)}`,
        key: `${this.translateService.getTranslation(headerKey)}`,
        width: 20,
        style: { alignment: { wrapText: true, vertical: 'middle', horizontal: 'center' } }
      };
    });
  }

  getReportingSubtabSection(start_date?: string, end_date?: string ) {
    let url = '/api/transactions/reporttopsection/';
    if (start_date && end_date) {
      url += '?start_date=' + start_date + '&end_date=' + end_date;
    }
    return this.api.get(url).pipe(
      map(response => {
        if (response?.error?.status || response?.status >= 400) {
          throw response;
        }
        return response.reports;
      }),
      catchError(error => {
        return this.errorHandler.handleError(error, []);
      })
    );
  }

  replaceUidOnQueryString(locationSearch: string): string {
    let queryParams = locationSearch ? new HttpParams({ fromString: locationSearch }) : new HttpParams();
    queryParams = <HttpParams>queryParams.set('uid', uuid());
    return `?${queryParams.toString()}`;
  }

  getNewUid(): string {
    return uuid();
  }

  getTransactionSummaryReportData(
    page: number = 1,
    pageSize: number = 20,
    transStatus: string,
    displayView: string,
    startDate: string,
    endDate: string,
    querySearch: string,
    sortBy?: string,
    sortDirection?: string,
    uid?: string) {
    let params = `page=${page}&page_size=${pageSize}`;
    if (transStatus) {
      params += `&status=${transStatus}`;
    }
    if (displayView) {
      params += `&view=${displayView}`;
    }
    if (startDate && endDate) {
      params += `&start_date=${startDate}&end_date=${endDate}`;
    }
    if (querySearch) {
      params += `&q=${querySearch}`;
    }
    if (sortBy) {
      params += `&sort_by=${sortBy}`;
    }
    if (sortDirection) {
      params += `&sort_dir=${sortDirection}`;
    }
    if (uid) {
      params += `&uid=${uid}`;
    }
    const api_url = transStatus === TransactionStatusFilters.CLOSED ? '/api/reports/report_details/18/' : '/api/transactions/reports/';
    return this.api.get(`${api_url}?${params}`).pipe(
      repeat({ delay: 3000 }), // Retry every 3 seconds
      filter((response: any) => {
        // Only continue retrying if the response is 'still working on it' or error status is 0 or 504
        if (
          (response && response.status === 'still working on it') ||
          (response?.error && [0, 504].includes(response.error.status))
        ) {
          return false; // keep retrying
        }
        return true; // stop retrying
      }),
      take(1), // take only the final valid response
      map((response: any) => {
        if (response?.error?.status || (response?.status >= 400 && response?.status !== 'still working on it')) {
          throw response;
        }
        if (transStatus === TransactionStatusFilters.CLOSED) {
          this.closedReportData(response.results);
        }
        return response;
      }),
      catchError(error => {
        return this.errorHandler.handleError(error, []);
      })
    );
  }

  downloadExcelReport(queryParams, status: any, closedReport: any) {
    let reportingDownloadService = '';
    switch (status) {
      case 'wip':
      case 'submitted':
        // tslint:disable-next-line:max-line-length
        reportingDownloadService = `/api/transactions/reportingdownload/${queryParams}&status=${status}&download=${closedReport}`;
        break;
      case 'closed':
        // tslint:disable-next-line:max-line-length
        reportingDownloadService = `/api/reports/report_details/18/${queryParams}&download=true`;
        break;
      default:
        break;
    }
    return this.api.get(reportingDownloadService)
      .pipe(
        repeat({ delay: 3000 }), // Repeat the request with a delay of 3 seconds
        filter((response) => { // When the status is 'still working on it' or there is a Time Out Then repeat the request
          if ((response && response?.status === 'still working on it') || (response?.error && [0, 504].includes(response?.error?.status))) {
            return false;
          }
          return true;
        }),
        take(1),
        map(response => {
          if (response?.error?.status || (response?.status >= 400 && response?.status !== 'still working on it')) {
            throw response;
          }
          if (status === TransactionStatusFilters.CLOSED) {
            this.downloadClosedReportData(response);
          }
          if ([TransactionStatusFilters.WIP, TransactionStatusFilters.SUBMITTED].includes(status)) {
            this.downloadWorkInProgressData(response);
          }
          return response;
        }),
        catchError(error => {
          return this.errorHandler.handleError(error, []);
        })
      );
  }

  refreshGrid() {
    this.refreshGridSubject.next(true);
  }

  tableViewCheck(view) {
    this.tableViewCheckSubject.next(view);
  }

  getSmallViewHeaders() {
    return [
      {
        header: this.translateService.getTranslation('FL_ID'), key: 'transaction_id', width: 30, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('INVOICE_NO'), key: 'invoice_id', width: 30, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('TRANSACTION_TYPE'), key: 'transaction_type', width: 30, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('INVOICE_DT'), key: 'date', width: 30, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('INVOICE_TYPE'), key: 'invoice_type', width: 30, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('TEAM_ID'), key: 'team', width: 30, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('CURRENCY'), key: 'currency', width: 30, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('OPPORTUNITY_NO').toUpperCase(), key: 'opportunity_number', width: 30, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('OPPORTUNITY_ID'), key: 'opportunity', width: 30, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('OPPORTUNITY_INSTALLMENT_ID'), key: 'opportunity_installment_id', width: 30, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        },
      },
      {
        header: this.translateService.getTranslation('OPPORTUNITY_SOURCE'), key: 'opportunity_source', width: 30, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('MIQ_PROPERTY_ID'), key: 'miq_property_id', width: 30, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('PROPERTY_GRID_ID'), key: 'grid_id', width: 30, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('FUNCTIONAL_UNIT'), key: 'functional_unit', width: 30, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('MANAGING_OFFICE'), key: 'managing_office', width: 20, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('PRIMARY_EMPLOYEE_ID').toUpperCase(),
        key: 'primary_employee_id',
        width: 30,
        style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('PRIMARY_EMPLOYEE_NAME').toUpperCase(),
        key: 'primary_employee_name',
        width: 30,
        style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('PRIMARY_EMPLOYEE_FUNCTIONAL_UNIT_ID'),
        key: 'primary_employee_functional_unit_id',
        width: 40,
        style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('PRIMARY_EMPLOYEE_FU_DESC').toUpperCase(),
        key: 'primary_employee_functional_unit',
        width: 50,
        style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('PRIMARY_EMPLOYEE_MANAGING_OFFICE_ID'),
        key: 'primary_employee_managing_office_id',
        width: 40,
        style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('PRIMARY_EMPLOYEE_MANAGING_OFFICE_DESCRIPTION'),
        key: 'primary_employee_managing_office',
        width: 50,
        style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('BILL_TO_CUSTOMER_ID'), key: 'bill_to_customer_id', width: 30, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('DEDICATED_CLIENT_NAME_BILL_TO_CUST'), key: 'dedicated_client_name_bill_to_cust', width: 40, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('BILL_TO_CUSTOMER_NAME'), key: 'bill_to_customer_name', width: 30, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('REPPED_CUSTOMER_ID'), key: 'repped_customer_id', width: 30, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('DEDICATED_CLIENT_NAME_REPPED_CUST'), key: 'dedicated_client_name_repped_cust', width: 35, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('REPPED_CUSTOMER'), key: 'represented_party', width: 30, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('REP_ROLE'), key: 'rep_role', width: 20, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('PARENT_COMPANY_NAME'), key: 'parent_company_name', width: 20, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('BUILDING_NAME'), key: 'building_name', width: 30, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('ADDRESS_LINE1'), key: 'address_line1', width: 30, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('ADDRESS_LINE2'), key: 'address_line2', width: 30, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('POSTAL_CODE'), key: 'postal_code', width: 30, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('SUBURB'), key: 'suburb', width: 30, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('DISTRICT'), key: 'district', width: 30, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('LOCALITY'), key: 'locality', width: 30, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('REGION'), key: 'region', width: 10, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('COUNTRY'), key: 'country', width: 30, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('PROPERTY_TYPE'), key: 'property_type', width: 30, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('SIZE'), key: 'size', width: 30, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('UOM'), key: 'size_measurement', width: 20, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('ROOMS'), key: 'rooms', width: 10, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('UNITS'), key: 'units', width: 10, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('AREA'), key: 'area', width: 10, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('CBRE_FEE_$'), key: 'cbre_fee', width: 16, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'left',
          }
        }
      },
      {
        header: this.translateService.getTranslation('CBRE_FEE_FUMO'), key: 'flat_amount', width: 30, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'left',
          }
        }
      },
      {
        header: this.translateService.getTranslation('CBRE_REVENUE_$'), key: 'cbre_revenue', width: 30, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'left'
          }
        }
      },
      {
        header: this.translateService.getTranslation('CBRE_REVENUE_FUMO'), key: 'actual_fee', width: 30, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'left',
          }
        }
      },
      {
        header: this.translateService.getTranslation('INVOICE_VALUE_$'), key: 'invoice_value_total', width: 16, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'left',
          }
        }
      },
      {
        header: this.translateService.getTranslation('INVOICE_VALUE_SPLIT_$'), key: 'invoice_value_split', width: 16, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'left',
          }
        }
      },
      {
        header: this.translateService.getTranslation('NOTES'), key: 'fee_notes', width: 30, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('INTERNAL_NOTES'),
        key: 'internal_notes',
        width: 30,
        style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('MASTER_BILLING_INVOICE'),
        key: 'master_billing_invoice',
        width: 30,
        style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('SALES_COMPLETION_DATE'), key: 'sale_completion_date', width: 30, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('CREDIT_REASON'), key: 'credit_reason', width: 30, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('CREDIT_NOTES'), key: 'credit_notes', width: 30, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('LOB'), key: 'lob', width: 30, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('SELLER_NAME'), key: 'seller_name', width: 30, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('PROPERTY_BASE_ID'), key: 'property_base_id', width: 30, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('IP_METRO'), key: 'ip_metro', width: 30, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('IS_DATA_CENTER_DEAL'), key: 'is_data_center_deal', width: 30, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('NO_OF_PROPERTIES'), key: 'no_of_properties', width: 30, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('CREATED_BY'), key: 'created_by', width: 30, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('SUBMITTED_BY'), key: 'submitted_by', width: 30, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('APPROVED_BY'), key: 'approved_by', width: 30, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
    ];
  }

  getFullTableViewHeaders() {
    return [
      {
        header: this.translateService.getTranslation('FL_ID'), key: 'transaction_id', width: 7, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('TRANSACTION_TYPE'), key: 'transaction_type', width: 30, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('INVOICE_DT'), key: 'date', width: 30, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('INVOICE_TYPE'), key: 'invoice_type', width: 30, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('TEAM_ID'), key: 'team', width: 30, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('CURRENCY'), key: 'currency', width: 30, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('OPPORTUNITY_NO').toUpperCase(), key: 'opportunity_number', width: 30, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('OPPORTUNITY_ID'), key: 'opportunity', width: 30, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('OPPORTUNITY_INSTALLMENT_ID'), key: 'opportunity_installment_id', width: 30, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        },
      },
      {
        header: this.translateService.getTranslation('OPPORTUNITY_SOURCE'), key: 'opportunity_source', width: 30, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('MIQ_PROPERTY_ID'), key: 'miq_property_id', width: 30, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('PROPERTY_GRID_ID'), key: 'grid_id', width: 30, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('FUNCTIONAL_UNIT'), key: 'functional_unit', width: 30, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('MANAGING_OFFICE'), key: 'managing_office', width: 20, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('PRODUCER_BROKER_EMPID'), key: 'broker_id', width: 20, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('PRODUCER_BROKER_NAME'), key: 'name', width: 24, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('PRODUCER_FUNCTIONAL_UNIT'), key: 'producer_functional_unit', width: 24, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('PRODUCER_FUNCTIONAL_UNIT_DESCRIPTION'), key: 'producer_functional_unit_descr', width: 24, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('PRODUCER_MANAGING_OFFICE'), key: 'producer_managing_office', width: 24, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('PRODUCER_MANAGING_OFFICE_DESCRIPTION'), key: 'producer_managing_office_descr', width: 24, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('PRODUCER_ROLE'), key: 'producer_role', width: 24, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('COMPANY_OFFICE'), key: 'company_office', width: 24, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('PARENT_COMPANY_NAME'), key: 'parent_company_name', width: 20, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('CONJUNCTIONAL_AGENT'), key: 'conjunctional_agent', width: 24, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('AFFILIATE_CODE'), key: 'affiliate_code', width: 24, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('BILL_TO_CUSTOMER_ID'), key: 'bill_to_customer_id', width: 24, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('DEDICATED_CLIENT_NAME_BILL_TO_CUST'), key: 'dedicated_client_name_bill_to_cust', width: 24, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('BILL_TO_CUSTOMER_NAME'), key: 'bill_to_party', width: 24, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('REPPED_CUSTOMER_ID'), key: 'repped_customer_id', width: 24, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('DEDICATED_CLIENT_NAME_REPPED_CUST'), key: 'dedicated_client_name_repped_cust', width: 24, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('OPERATOR'), key: 'operator', width: 24, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('REPPED_CUSTOMER'), key: 'represented_party', width: 24, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('REP_ROLE'), key: 'rep_role', width: 20, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('COUNTER_PARTY_NAME'), key: 'counter_party', width: 30, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('OTHER_PARTY'), key: 'other_party', width: 30, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('BUILDING_NAME'), key: 'building_name', width: 30, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('ADDRESS_LINE1'), key: 'address_line1', width: 30, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('ADDRESS_LINE2'), key: 'address_line2', width: 30, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('POSTAL_CODE'), key: 'postal_code', width: 30, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('SUBURB'), key: 'suburb', width: 30, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('DISTRICT'), key: 'district', width: 30, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('LOCALITY'), key: 'locality', width: 30, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('REGION'), key: 'region', width: 10, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('COUNTRY'), key: 'country', width: 10, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('PROPERTY_TYPE'), key: 'property_type', width: 20, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('SIZE'), key: 'size', width: 10, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('UOM'), key: 'size_measurement', width: 20, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('ROOMS'), key: 'rooms', width: 10, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('UNITS'), key: 'units', width: 10, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('AREA'), key: 'area', width: 10, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('CBRE_FEE_$'), key: 'cbre_fee', width: 16, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'left',
          }
        }
      },
      {
        header: this.translateService.getTranslation('CBRE_FEE_FUMO'), key: 'flat_amount', width: 30, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'left',
          }
        }
      },
      {
        header: this.translateService.getTranslation('CBRE_REVENUE_$'), key: 'cbre_revenue', width: 16, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'left'
          }
        }
      },
     {
        header: this.translateService.getTranslation('CBRE_REVENUE_FUMO'), key: 'actual_fee', width: 16, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'left'
          }
        }
      },
      {
        header: this.translateService.getTranslation('INVOICE_VALUE_$'), key: 'invoice_value_total', width: 16, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'left',
          }
        }
      },
      {
        header: this.translateService.getTranslation('INVOICE_VALUE_SPLIT_$'), key: 'invoice_value_split', width: 16, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'left',
          }
        }
      },
      {
        header: this.translateService.getTranslation('NOTES'), key: 'fee_notes', width: 30, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('INTERNAL_NOTES'),
        key: 'internal_notes',
        width: 30,
        style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('MASTER_BILLING_INVOICE'),
        key: 'master_billing_invoice',
        width: 30,
        style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('SALES_COMPLETION_DATE'), key: 'sale_completion_date', width: 30, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('CREDIT_REASON'), key: 'credit_reason', width: 30, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('CREDIT_NOTES'), key: 'credit_notes', width: 30, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('LOB'), key: 'lob', width: 30, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('SELLER_NAME'), key: 'seller_name', width: 30, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('PROPERTY_BASE_ID'), key: 'property_base_id', width: 30, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('IP_METRO'), key: 'ip_metro', width: 30, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('IS_DATA_CENTER_DEAL'), key: 'is_data_center_deal', width: 30, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('NO_OF_PROPERTIES'), key: 'no_of_properties', width: 30, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('CREATED_BY'), key: 'created_by', width: 30, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('SUBMITTED_BY'), key: 'submitted_by', width: 30, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('APPROVED_BY'), key: 'approved_by', width: 30, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
    ];
  }

  getClosedSmallViewHeaders() {
    return [
      {
        header: this.translateService.getTranslation('FL_ID'), key: 'finishline_id', width: 30, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('INVOICE_NO'), key: 'invoice_no', width: 30, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('TRANSACTION_TYPE'), key: 'transaction_type', width: 30, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('INVOICE_DT'), key: 'invoice_date', width: 30, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('INVOICE_TYPE'), key: 'invoice_type', width: 30, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('TEAM_ID'), key: 'team', width: 30, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('CURRENCY'), key: 'currency', width: 30, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('OPPORTUNITY_NO').toUpperCase(), key: 'opportunity_number', width: 30, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('OPPORTUNITY_ID'), key: 'opportunity', width: 30, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('OPPORTUNITY_INSTALLMENT_ID'), key: 'opportunity_installment_id', width: 30, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        },
      },
      {
        header: this.translateService.getTranslation('OPPORTUNITY_SOURCE'), key: 'opportunity_source', width: 30, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('MIQ_PROPERTY_ID'), key: 'miq_property_id', width: 30, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('PROPERTY_GRID_ID'), key: 'grid_id', width: 30, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('FUNCTIONAL_UNIT'), key: 'functional_unit', width: 30, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('MANAGING_OFFICE'), key: 'managing_office', width: 20, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('PRIMARY_EMPLOYEE_ID').toUpperCase(),
        key: 'primary_employee_id',
        width: 30,
        style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('PRIMARY_EMPLOYEE_NAME').toUpperCase(),
        key: 'primary_employee_name',
        width: 30,
        style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('PRIMARY_EMPLOYEE_FUNCTIONAL_UNIT_ID'),
        key: 'primary_employee_functional_unit_id',
        width: 40,
        style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('PRIMARY_EMPLOYEE_FU_DESC').toUpperCase(),
        key: 'primary_employee_functional_unit',
        width: 50,
        style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('PRIMARY_EMPLOYEE_MANAGING_OFFICE_ID'),
        key: 'primary_employee_managing_office_id',
        width: 40,
        style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('PRIMARY_EMPLOYEE_MANAGING_OFFICE_DESCRIPTION'),
        key: 'primary_employee_managing_office',
        width: 50,
        style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('BILL_TO_CUSTOMER_ID'), key: 'bill_to_customer_id', width: 30, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('DEDICATED_CLIENT_NAME_BILL_TO_CUST'), key: 'dedicated_client_name_bill_to_cust', width: 40, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('BILL_TO_CUSTOMER_NAME'), key: 'bill_to_customer_name', width: 30, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('REPPED_CUSTOMER_ID'), key: 'repped_customer_id', width: 30, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('DEDICATED_CLIENT_NAME_REPPED_CUST'), key: 'dedicated_client_name_repped_cust', width: 40, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('REPPED_CUSTOMER'), key: 'repped_customer', width: 30, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('REP_ROLE'), key: 'rep_role', width: 20, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('PARENT_COMPANY_NAME'), key: 'parent_company_name', width: 20, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('BUILDING_NAME'), key: 'building_name', width: 30, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('ADDRESS_LINE1'), key: 'address_line1', width: 30, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('ADDRESS_LINE2'), key: 'address_line2', width: 30, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('POSTAL_CODE'), key: 'postal_code', width: 30, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('SUBURB'), key: 'suburb', width: 30, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('DISTRICT'), key: 'district', width: 30, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('LOCALITY'), key: 'locality', width: 30, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('REGION'), key: 'region', width: 10, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('COUNTRY'), key: 'country', width: 30, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('PROPERTY_TYPE'), key: 'property_type', width: 30, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('ADDRESS_LINE2'), key: 'address_line2', width: 30, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('POSTAL_CODE'), key: 'postal_code', width: 30, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('SUBURB'), key: 'suburb', width: 30, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('DISTRICT'), key: 'district', width: 30, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('LOCALITY'), key: 'locality', width: 30, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('REGION'), key: 'region', width: 10, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('COUNTRY'), key: 'country', width: 30, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('PROPERTY_TYPE'), key: 'property_type', width: 30, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('SIZE'), key: 'size', width: 10, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('UOM'), key: 'uom', width: 15, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('ROOMS'), key: 'rooms', width: 10, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('UNITS'), key: 'units', width: 10, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('AREA'), key: 'area', width: 10, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('CBRE_FEE_$'), key: 'cbre_fee_total', width: 16, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'left',
          }
        }
      },
      {
        header: this.translateService.getTranslation('CBRE_FEE_FUMO'), key: 'cbre_fee_fumo_balance', width: 30, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'left',
          }
        }
      },
      {
        header: this.translateService.getTranslation('CBRE_REVENUE_$'), key: 'cbre_revenue_total', width: 30, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'left'
          }
        }
      },
      {
        header: this.translateService.getTranslation('CBRE_REVENUE_FUMO'), key: 'cbre_revenue_fumo_balance', width: 30, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'left',
          }
        }
      },
      {
        header: this.translateService.getTranslation('INVOICE_VALUE_$'), key: 'invoice_value_total', width: 16, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'left',
          }
        }
      },
      {
        header: this.translateService.getTranslation('INVOICE_VALUE_SPLIT_$'), key: 'invoice_value_split', width: 16, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'left',
          }
        }
      },
      {
        header: this.translateService.getTranslation('NOTES'), key: 'notes', width: 30, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('INTERNAL_NOTES'),
        key: 'internal_notes',
        width: 30,
        style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('MASTER_BILLING_INVOICE'),
        key: 'master_billing_invoice',
        width: 30,
        style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('SALES_COMPLETION_DATE'), key: 'sale_completion_date', width: 30, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('CREDIT_REASON'), key: 'credit_reason', width: 30, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('CREDIT_NOTES'), key: 'credit_notes', width: 30, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('LOB'), key: 'lob', width: 30, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('SELLER_NAME'), key: 'seller_name', width: 30, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('PROPERTY_BASE_ID'), key: 'property_base_id', width: 30, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('IP_METRO'), key: 'ip_metro', width: 30, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('IS_DATA_CENTER_DEAL'), key: 'is_data_center_deal', width: 30, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('NO_OF_PROPERTIES'), key: 'no_of_properties', width: 30, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('CREATED_BY'), key: 'created_by', width: 30, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('SUBMITTED_BY'), key: 'submitted_by', width: 30, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('APPROVED_BY'), key: 'approved_by', width: 30, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
    ];
  }

  getClosedFullTableViewHeaders() {
    return [
      {
        header: this.translateService.getTranslation('FL_ID'), key: 'finishline_id', width: 7, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('INVOICE_NO'), key: 'invoice_no', width: 20, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('TRANSACTION_TYPE'), key: 'transaction_type', width: 30, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('INVOICE_DT'), key: 'invoice_date', width: 30, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('INVOICE_TYPE'), key: 'invoice_type', width: 30, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('TEAM_ID'), key: 'team', width: 30, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('CURRENCY'), key: 'currency', width: 30, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('OPPORTUNITY_NO').toUpperCase(), key: 'opportunity_number', width: 30, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('OPPORTUNITY_ID'), key: 'opportunity', width: 30, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('OPPORTUNITY_INSTALLMENT_ID'), key: 'opportunity_installment_id', width: 30, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        },
      },
      {
        header: this.translateService.getTranslation('OPPORTUNITY_SOURCE'), key: 'opportunity_source', width: 30, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('MIQ_PROPERTY_ID'), key: 'miq_property_id', width: 30, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('PROPERTY_GRID_ID'), key: 'grid_id', width: 30, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('FUNCTIONAL_UNIT'), key: 'functional_unit', width: 30, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('MANAGING_OFFICE'), key: 'managing_office', width: 20, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('PRODUCER_BROKER_EMPID'), key: 'producer_employee_id', width: 20, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('PRODUCER_BROKER_NAME'), key: 'producer_name', width: 24, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('PRODUCER_FUNCTIONAL_UNIT'), key: 'producer_functional_unit', width: 24, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('PRODUCER_FUNCTIONAL_UNIT_DESCRIPTION'), key: 'producer_functional_unit_description', width: 24, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('PRODUCER_MANAGING_OFFICE'), key: 'producer_managing_office', width: 24, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('PRODUCER_MANAGING_OFFICE_DESCRIPTION'), key: 'producer_managing_office_description', width: 24, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('PRODUCER_ROLE'), key: 'producer_role', width: 24, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('COMPANY_OFFICE'), key: 'company_office', width: 24, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('PARENT_COMPANY_NAME'), key: 'parent_company_name', width: 20, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('CONJUNCTIONAL_AGENT'), key: 'conjunctional_agent', width: 24, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('AFFILIATE_CODE'), key: 'affiliate_code', width: 24, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('BILL_TO_CUSTOMER_ID'), key: 'bill_to_customer_id', width: 30, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('DEDICATED_CLIENT_NAME_BILL_TO_CUST'), key: 'dedicated_client_name_bill_to_cust', width: 30, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('BILL_TO_CUSTOMER_NAME'), key: 'bill_to_customer_name', width: 30, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('REPPED_CUSTOMER_ID'), key: 'repped_customer_id', width: 30, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('DEDICATED_CLIENT_NAME_REPPED_CUST'), key: 'dedicated_client_name_repped_cust', width: 30, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('OPERATOR'), key: 'operator', width: 24, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('REPPED_CUSTOMER'), key: 'repped_customer', width: 24, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('REP_ROLE'), key: 'rep_role', width: 24, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('COUNTER_PARTY_NAME'), key: 'counter_party', width: 30, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('OTHER_PARTY'), key: 'other_party', width: 30, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('BUILDING_NAME'), key: 'building_name', width: 30, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('ADDRESS_LINE1'), key: 'address_line1', width: 30, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('ADDRESS_LINE2'), key: 'address_line2', width: 30, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('POSTAL_CODE'), key: 'postal_code', width: 30, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('SUBURB'), key: 'suburb', width: 30, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('DISTRICT'), key: 'district', width: 30, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('LOCALITY'), key: 'locality', width: 30, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('REGION'), key: 'region', width: 10, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('COUNTRY'), key: 'country', width: 10, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('PROPERTY_TYPE'), key: 'property_type', width: 20, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('SIZE'), key: 'size', width: 10, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('UOM'), key: 'uom', width: 15, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('ROOMS'), key: 'rooms', width: 10, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('UNITS'), key: 'units', width: 10, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('AREA'), key: 'area', width: 10, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('CBRE_FEE_$'), key: 'cbre_fee_total', width: 16, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'left',
          }
        }
      },
      {
        header: this.translateService.getTranslation('CBRE_FEE_FUMO'), key: 'cbre_fee_fumo_balance', width: 30, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'left',
          }
        }
      },
      {
        header: this.translateService.getTranslation('CBRE_REVENUE_$'), key: 'cbre_revenue_total', width: 16, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'left'
          }
        }
      },
      {
        header: this.translateService.getTranslation('CBRE_REVENUE_FUMO'), key: 'cbre_revenue_fumo_balance', width: 16, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'left'
          }
        }
      },
      {
        header: this.translateService.getTranslation('BALANCE'), key: 'balance', width: 16, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'left'
          }
        }
      },
      {
        header: this.translateService.getTranslation('INVOICE_VALUE_$'), key: 'invoice_value_total', width: 16, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'left',
          }
        }
      },
      {
        header: this.translateService.getTranslation('INVOICE_VALUE_SPLIT_$'), key: 'invoice_value_split', width: 16, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'left',
          }
        }
      },
      {
        header: this.translateService.getTranslation('NOTES'), key: 'notes', width: 30, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('INTERNAL_NOTES'),
        key: 'internal_notes',
        width: 30,
        style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('MASTER_BILLING_INVOICE'),
        key: 'master_billing_invoice',
        width: 30,
        style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('SALES_COMPLETION_DATE'), key: 'sale_completion_date', width: 30, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('CREDIT_REASON'), key: 'credit_reason', width: 30, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('CREDIT_NOTES'), key: 'credit_notes', width: 30, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('LOB'), key: 'lob', width: 30, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('SELLER_NAME'), key: 'seller_name', width: 30, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('PROPERTY_BASE_ID'), key: 'property_base_id', width: 30, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('IP_METRO'), key: 'ip_metro', width: 30, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('IS_DATA_CENTER_DEAL'), key: 'is_data_center_deal', width: 30, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('NO_OF_PROPERTIES'), key: 'no_of_properties', width: 30, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('CREATED_BY'), key: 'created_by', width: 30, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('SUBMITTED_BY'), key: 'submitted_by', width: 30, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
      {
        header: this.translateService.getTranslation('APPROVED_BY'), key: 'approved_by', width: 30, style: {
          alignment: {
            wrapText: true,
            vertical: 'middle', horizontal: 'center'
          }
        }
      },
    ];
  }

  downloadClosedReportData(transaction: any) {
    let rows: DataObject[] = [];
    if (!transaction || (transaction && transaction.error)) {
      return;
    }
    if (transaction.results) {
      rows = transaction.results;
    } else {
      rows = transaction;
    }
    rows.forEach((field: DataObject) => {
      let spaceValues: any;
      let propertyType = !!field.property_type ? field.property_type : '';

      if (field.spacetypes && field.spacetypes.length) {
        spaceValues = field.spacetypes;
      }

      field.finishline_id = !!field.finishline_id ? field.finishline_id : '';
      field.repped_customer = !!field.repped_customer ? field.repped_customer : '';
      field.other_party = !!field.other_party ? field.other_party : null;
      field.building_name = !!field.building_name ? field.building_name : '';
      field.address_line1 = !!field.address_line1 ? field.address_line1 : null;
      field.notes = !!field.notes ? field.notes : null;
      field.internal_notes = !!field.internal_notes ? field.internal_notes : null;
      field.country = !!field.country ? field.country : '';
      field.region = !!field.region ? field.region : '';
      field.property_type = propertyType;
      field.conjunctional_agent = !!field.conjunctional_agent ? field.conjunctional_agent : '';
      field.invoice_no = !!field.invoice_no ? field.invoice_no : '';
    });
  }

  closedReportData(transaction: any) {
    let rows = [];
    if (transaction && transaction.error && transaction.error.status !== 200) {
      return rows;
    }
    if (transaction.results) {
      rows = transaction.results;
    } else {
      rows = transaction;
    }
    rows.forEach((field: DataObject) => {
      let spaceValues: any;
      let propertyType = !!field.property_type ? field.property_type : '';

      if (field.spacetypes && field.spacetypes.length) {
        spaceValues = field.spacetypes;
      }

      field.finishline_id = !!field.finishline_id ? field.finishline_id : '';
      field.repped_customer = !!field.repped_customer ? field.repped_customer : '';
      field.other_party = !!field.other_party ? field.other_party : null;
      field.building_name = !!field.building_name ? field.building_name : '';
      field.address_line1 = !!field.address_line1 ? field.address_line1 : null;
      field.notes = !!field.notes ? field.notes : null;
      field.internal_notes = !!field.internal_notes ? field.internal_notes : null;
      field.country = !!field.country ? field.country : '';
      field.region = !!field.region ? field.region : '';
      field.property_type = propertyType;
      field.conjunctional_agent = !!field.conjunctional_agent ? field.conjunctional_agent : '';
      field.invoice_no = !!field.invoice_no ? field.invoice_no : '';
    });
  }

  downloadWorkInProgressData(transaction: DataObject) {
    if (!transaction || (transaction && transaction.error)) {
      return;
    }
    transaction.forEach((field: DataObject) => {
      field.flat_amount = field.flat_amount === null ? 0.00 : field.flat_amount;
      field.cbre_fee = field.cbre_fee === null ? 0.00 : field.cbre_fee;
      field.size = field.size === null ? 0.00 : field.size;
      field.units = field.units === null ? 0.00 : field.units;
      field.rooms = field.rooms === null ? 0.00 : field.rooms;
      field.area = field.area === null ? 0.00 : field.area;
    });
  }

  submittedData(data: any): any {
    if (data && data.error && data.error.status !== 200) {
      return [];
    }
    data.forEach((row: any) => {
      row.date = !!row.date ? this.helperService.transformDate(row.date) : '';
    });
  }

  getPropertiesSize(propertyType: string = '', propertiesArray: any[] = []): any {
    const totalPropertySize = [];
    const propertyTypeKey = this.getPropertyOrSpaceKeyType(propertyType);
    totalPropertySize[propertyTypeKey] = (propertiesArray && propertiesArray.length > 0) ?
      propertiesArray.reduce((acc, property) => acc + +property[propertyTypeKey], 0) : 0;
    return totalPropertySize;
  }

  getPropertySizeBySpaceType(propertyType: string = '', spaceTypes: any[] = []): any {
    const spaceTypeTotal = [];
    const spaceTypeKey = this.getPropertyOrSpaceKeyType(propertyType);
    spaceTypeTotal[spaceTypeKey] = (spaceTypes && spaceTypes.length > 0) ?
      spaceTypes.reduce((acc, spaceType) => acc + +spaceType[spaceTypeKey], 0) : 0;
    return spaceTypeTotal;
  }

  getPropertyOrSpaceKeyType(type: string = ''): string {
    let keyType = '';
    type = type.toLowerCase();
    switch (type) {
      case 'land':
        keyType = 'land';
        break;
      case 'hotel':
        keyType = 'rooms';
        break;
      case 'residential':
        keyType = 'units';
        break;
      default:
        keyType = 'size';
        break;
    }
    return keyType;
  }

    // Gets the Pagination Parameters from the URL
  getPaginationParameters(defaultSearchParams = {}) {
    const searchParams = new URLSearchParams(defaultSearchParams);
    const searchPage = searchParams.get('page') || ReportingFilterObjInit.paginationPage;
    const searchPageSize = searchParams.get('page_size') || ReportingFilterObjInit.paginationPageSize;
    return {
      page: +searchPage,
      pageSize: +searchPageSize,
    };
  }

  // Gets the Sorting Parameters from the URL
  getSortingParameters(defaultSearchParams = {}) {
    const searchParams = new URLSearchParams(defaultSearchParams);
    return {
      sortBy: searchParams.get('sort_by') || ReportingFilterObjInit.sortBy,
      sortDir: searchParams.get('sort_dir') || ReportingFilterObjInit.sortDir,
    };
  }

  // Gets the Reporting Subtabs Parameters from the URL
  getReportingSubtabsParameters(defaultSearchParams = {}) {
    const searchParams = new URLSearchParams(defaultSearchParams);
    return {
      start_date: searchParams.get('start_date') || ReportingFilterObjInit.startDate,
      end_date: searchParams.get('end_date') || ReportingFilterObjInit.endDate,
      q: searchParams.get('q') || ReportingFilterObjInit.querySearch,
      view: searchParams.get('view') || ReportingFilterObjInit.displayView,
      date_range: searchParams.get('date_range') || ReportingFilterObjInit.dateRange,
      uid: searchParams.get('uid') || ReportingFilterObjInit.uid,
    };
  }
  getDateLabel(transactionStatus: string): string {
    switch (transactionStatus) {
      case 'wip': return this.translateService.getTranslation('DATE_CREATED');
      case 'submitted': return this.translateService.getTranslation('DATE_SUBMITTED');
      case 'closed': return this.translateService.getTranslation('INVOICE_DT');
      default: return this.translateService.getTranslation('DATE_CREATED');
    }
  }

  getLandingPageHeaders() {
    // Return only the specific headers requested for landing page transactions
    return [
      {
        header: this.translateService.getTranslation('FL_ID'),
        key: 'id',
        width: 10,
        style: { alignment: { wrapText: true, vertical: 'middle', horizontal: 'center' } }
      },
      {
        header: this.translateService.getTranslation('INVOICE_ID'),
        key: 'invoice_id',
        width: 15,
        style: { alignment: { wrapText: true, vertical: 'middle', horizontal: 'center' } }
      },
      {
        header: this.translateService.getTranslation('DATE_CREATED'),
        key: 'date',
        width: 15,
        style: { alignment: { wrapText: true, vertical: 'middle', horizontal: 'center' } }
      },
      {
        header: this.translateService.getTranslation('COMPANY'),
        key: 'represented_party',
        width: 25,
        style: { alignment: { wrapText: true, vertical: 'middle', horizontal: 'left' } }
      },
      {
        header: this.translateService.getTranslation('FUNCTIONAL_UNIT'),
        key: 'functional_unit',
        width: 15,
        style: { alignment: { wrapText: true, vertical: 'middle', horizontal: 'center' } }
      },
      {
        header: this.translateService.getTranslation('MANAGING_OFFICE'),
        key: 'managing_office',
        width: 15,
        style: { alignment: { wrapText: true, vertical: 'middle', horizontal: 'center' } }
      },
      {
        header: this.translateService.getTranslation('AMOUNT'),
        key: 'cbre_fee',
        width: 15,
        style: { alignment: { wrapText: true, vertical: 'middle', horizontal: 'right' } }
      },
      {
        header: this.translateService.getTranslation('NOTE'),
        key: 'peoplesoft_notes',
        width: 30,
        style: { alignment: { wrapText: true, vertical: 'middle', horizontal: 'left' } }
      },
      {
        header: this.translateService.getTranslation('CREATED_BY'),
        key: 'owner',
        width: 20,
        style: { alignment: { wrapText: true, vertical: 'middle', horizontal: 'left' } }
      },
      {
        header: this.translateService.getTranslation('TRANSACTION_STATUS'),
        key: 'status_name',
        width: 20,
        style: { alignment: { wrapText: true, vertical: 'middle', horizontal: 'center' } }
      }
    ];
  }
}
